// The words of a search, inside the document it found (decision 27 of 2/11): a bar
// above the text with each word and how many times the whole text has it
// («Davide 3 · Ciao 1»), the arrows that go to its previous and next place, the
// words marked. The text is shown a piece at a time (NewsReader, ParliamentReader):
// to reach a place not shown yet the bar opens the next pieces, as «Continua a
// leggere» does, then scrolls to it. The marks are the browser's own highlights
// (CSS Custom Highlight API: ::highlight in globals.css), never a change to the
// DOM React owns; where the browser has none, the bar still goes to each place.
import { useCallback, useEffect, useMemo, useState } from "react";
import { Button } from "@heroui/react";

import { Icon } from "../Icon";
import { countIn, foldText, termPattern, type FinderTerm } from "../../lib/finder";
import { CONTINUE_READING } from "./NewsReader";

/** The places of the word in the text shown, as ranges, in the order of the text. */
function rangesIn(root: HTMLElement, term: FinderTerm): Range[] {
  const pattern = termPattern(term);
  const out: Range[] = [];
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    const text = node.textContent ?? "";
    for (const m of foldText(text).matchAll(pattern)) {
      const range = document.createRange();
      range.setStart(node, m.index);
      range.setEnd(node, m.index + m[0].length);
      out.push(range);
    }
  }
  return out;
}

const textRoot = () => document.querySelector<HTMLElement>('section[aria-label="Testo"]');
const moreButton = () =>
  [...document.querySelectorAll<HTMLButtonElement>('section[aria-label="Testo"] button')].find(
    (b) => b.textContent?.trim() === CONTINUE_READING,
  );
const nextFrame = () => new Promise<void>((resolve) => requestAnimationFrame(() => setTimeout(resolve, 0)));

type HighlightRegistry = { set: (name: string, value: unknown) => void; delete: (name: string) => void };
type HighlightCtor = new (...ranges: Range[]) => unknown;
const highlights = (): { registry: HighlightRegistry; Ctor: HighlightCtor } | null => {
  const css = (globalThis as { CSS?: { highlights?: HighlightRegistry } }).CSS;
  const Ctor = (globalThis as { Highlight?: HighlightCtor }).Highlight;
  return css?.highlights && Ctor ? { registry: css.highlights, Ctor } : null;
};

type Place = { term: number; k: number };

/** The bar of the words above the text, and the marks in it. */
export function SearchFinder({ terms, text }: { terms: FinderTerm[]; text: string }) {
  const counts = useMemo(() => terms.map((t) => countIn(text, t)), [terms, text]);
  // the word the text has first: where the document opens
  const opening = useMemo(() => {
    const folded = foldText(text);
    let best = -1;
    let at = Infinity;
    terms.forEach((t, i) => {
      const m = termPattern(t).exec(folded);
      if (m && m.index < at) {
        at = m.index;
        best = i;
      }
    });
    return best;
  }, [terms, text]);
  const [place, setPlace] = useState<Place | null>(null);
  const [busy, setBusy] = useState(false);

  /** Marks every word in the text shown, the current place apart. */
  const mark = useCallback(
    (current: Range | null) => {
      const api = highlights();
      const root = textRoot();
      if (!api || !root) return;
      api.registry.set("ricerca", new api.Ctor(...terms.flatMap((t) => rangesIn(root, t))));
      if (current) api.registry.set("ricerca-attuale", new api.Ctor(current));
      else api.registry.delete("ricerca-attuale");
    },
    [terms],
  );

  /** Goes to the k-th place of a word: the next pieces opened until it is shown, then scrolled to. */
  const go = useCallback(
    async (target: Place) => {
      setBusy(true);
      try {
        for (;;) {
          const root = textRoot();
          if (!root) return;
          const ranges = rangesIn(root, terms[target.term]!);
          const range = ranges[target.k];
          if (range) {
            const box = range.getBoundingClientRect();
            window.scrollTo({ top: window.scrollY + box.top - window.innerHeight / 3, behavior: "smooth" });
            mark(range);
            setPlace(target);
            return;
          }
          // the next piece: its button may be away for a frame while the reader renders
          let more = moreButton();
          for (let wait = 0; !more && wait < 20; wait += 1) {
            await nextFrame();
            more = moreButton();
            if (rangesIn(textRoot() ?? document.body, terms[target.term]!).length > target.k) break;
          }
          if (!more) {
            if (rangesIn(textRoot() ?? document.body, terms[target.term]!).length > target.k) continue;
            setPlace(target);
            return;
          }
          more.click();
          await nextFrame();
        }
      } finally {
        setBusy(false);
      }
    },
    [terms, mark],
  );

  // at first: the first place of the first word the text has
  useEffect(() => {
    // after the text is in the page, and not inside the effect itself
    const start = opening >= 0 ? setTimeout(() => void go({ term: opening, k: 0 }), 0) : undefined;
    return () => {
      clearTimeout(start);
      highlights()?.registry.delete("ricerca");
      highlights()?.registry.delete("ricerca-attuale");
    };
    // once per document and words
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [terms, text]);

  // the pieces opened by reading on get their marks too
  useEffect(() => {
    const root = textRoot();
    if (!root || typeof MutationObserver !== "function") return;
    const observer = new MutationObserver(() => mark(null));
    observer.observe(root, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [mark]);

  if (terms.length === 0) return null;
  const current = place ?? { term: Math.max(0, counts.findIndex((n) => n > 0)), k: -1 };
  const step = (by: number) => {
    const n = counts[current.term] ?? 0;
    if (n === 0) return;
    void go({ term: current.term, k: (current.k + by + n) % n });
  };

  return (
    <div
      role="toolbar"
      aria-label="Parole cercate nel testo"
      className="sticky top-0 z-10 flex flex-wrap items-center gap-2 rounded-lg bg-white px-[14px] py-2 shadow-field"
    >
      <span className="text-xs leading-5 text-muted">Parole cercate:</span>
      {terms.map((t, i) => {
        const active = i === current.term;
        return (
          <button
            key={`${t.label}-${i}`}
            type="button"
            aria-pressed={active}
            disabled={counts[i] === 0 || busy}
            onClick={() => void go({ term: i, k: 0 })}
            className={`flex h-6 cursor-pointer items-center gap-1 rounded-xl px-2.5 text-xs leading-5 font-medium disabled:cursor-default disabled:opacity-50 ${
              active ? "bg-accent text-default" : "bg-accent-soft text-accent"
            }`}
          >
            {t.label}
            <span className={active ? "" : "text-muted"}>{counts[i]}</span>
          </button>
        );
      })}
      <span className="ml-auto flex items-center gap-1.5">
        {(counts[current.term] ?? 0) > 0 && (
          <span aria-live="polite" className="text-xs leading-5 text-muted">
            {current.k >= 0 ? current.k + 1 : 0} di {counts[current.term]}
          </span>
        )}
        <Button
          isIconOnly
          size="sm"
          aria-label="Precedente"
          isDisabled={busy || !(counts[current.term] ?? 0)}
          onPress={() => step(-1)}
          className="size-7 min-w-7 rounded-2xl bg-white shadow-field"
        >
          <Icon name="chevronUp" />
        </Button>
        <Button
          isIconOnly
          size="sm"
          aria-label="Successiva"
          isDisabled={busy || !(counts[current.term] ?? 0)}
          onPress={() => step(1)}
          className="size-7 min-w-7 rounded-2xl bg-white shadow-field"
        >
          <Icon name="chevronDown" />
        </Button>
      </span>
    </div>
  );
}
